/**
 * Stable machine key from a human label: lower-case, runs of anything that
 * isn't a letter or digit become one underscore, no leading/trailing
 * underscores ("Father's CNIC" → "father_s_cnic"). Used for form fields,
 * document checklist items, merit criteria and interview rounds.
 *
 * `maxLength` caps the key; leave it unset where existing saved keys were
 * made without a cap, so they keep matching.
 */
export const toFieldKey = (label: string, fallback: string, maxLength?: number): string => {
  const key = label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return (maxLength ? key.slice(0, maxLength) : key) || fallback;
};

/**
 * Give every item in a settings list its key, so staff never type one:
 *   - an item that already has a key keeps it — saved records (uploaded
 *     documents, marks, accepted terms, form answers) point at it, so it
 *     never changes, even when the item is renamed;
 *   - an item without one gets a key made from its label, made unique
 *     within the list ("photo", "photo_2", …).
 */
export function assignKeys<T extends { key?: string }>(
  items: T[],
  labelOf: (item: T) => string,
  fallback: string,
  maxLength?: number,
): T[] {
  const taken = new Set(items.map(i => i.key).filter((k): k is string => !!k));
  return items.map(item => {
    if (item.key) return item;
    const base = toFieldKey(labelOf(item) || '', fallback, maxLength);
    let key = base;
    for (let n = 2; taken.has(key); n += 1) key = `${base}_${n}`;
    taken.add(key);
    return { ...item, key };
  });
}
